import {
    FORMAT_PROFILE_OPTIONS,
    LICENSE_TYPE_OPTIONS,
} from "./availsPlusOptions";

export type ColumnKey =
    | "slate_status"
    | "roku_status"
    | "name"
    | "reference_id"
    | "content_type"
    | "license_type"
    | "format_profile"
    | "hdr"
    | "playback_start_date"
    | "playback_end_date"
    | "coming_soon_date"
    | "branding_rights"
    | "license_rights_description"
    | "licensor"
    | "season_number"
    | "episode_number"
    | "episode_count"
    | "season_count"
    | "series_ref_id"
    | "season_ref_id"
    | "series_title"
    | "season_title"
    | "caption_included";

export interface ColumnDef {
    key: ColumnKey;
    label: string;
    sortable?: boolean;
    minWidth?: number;
    defaultVisible?: boolean;
}

export const ALL_COLUMN_DEFS: ColumnDef[] = [
    { key: "slate_status", label: "Slate", minWidth: 108, defaultVisible: true },
    { key: "roku_status", label: "Roku", minWidth: 108, defaultVisible: true },
    { key: "name", label: "Title", sortable: true, minWidth: 200, defaultVisible: true },
    { key: "reference_id", label: "Ref ID", sortable: true, minWidth: 140, defaultVisible: true },
    { key: "series_title", label: "Series Title", sortable: true, minWidth: 160, defaultVisible: true },
    { key: "season_title", label: "Season Title", sortable: true, minWidth: 160, defaultVisible: true },
    { key: "content_type", label: "Content Type", sortable: true, minWidth: 110, defaultVisible: true },
    { key: "license_type", label: "License Type", sortable: true, minWidth: 110, defaultVisible: true },
    { key: "format_profile", label: "Format Profile", sortable: true, minWidth: 110, defaultVisible: true },
    { key: "hdr", label: "HDR10", sortable: true, minWidth: 90, defaultVisible: true },
    { key: "playback_start_date", label: "Playback Start Date", sortable: true, minWidth: 200, defaultVisible: true },
    { key: "playback_end_date", label: "Playback End Date", sortable: true, minWidth: 200, defaultVisible: true },
    { key: "coming_soon_date", label: "Announce Date", sortable: true, minWidth: 220, defaultVisible: true },
    { key: "branding_rights", label: "Branding Rights", sortable: true, minWidth: 120, defaultVisible: true },
    {
        key: "license_rights_description",
        label: "License Rights Description",
        sortable: true,
        minWidth: 160,
        defaultVisible: true,
    },
    { key: "licensor", label: "Licensor/Studio", sortable: true, minWidth: 140, defaultVisible: true },
    { key: "caption_included", label: "CaptionIncluded", sortable: true, minWidth: 130, defaultVisible: false },
    { key: "season_number", label: "Season Number", minWidth: 120, defaultVisible: false },
    { key: "episode_number", label: "Episode Number", minWidth: 120, defaultVisible: false },
    { key: "episode_count", label: "Episode Count", minWidth: 120, defaultVisible: false },
    { key: "season_count", label: "Season Count", minWidth: 120, defaultVisible: false },
    { key: "series_ref_id", label: "Series Ref ID", minWidth: 160, defaultVisible: false },
    { key: "season_ref_id", label: "Season Ref ID", minWidth: 160, defaultVisible: false },
];

export const DEFAULT_COLUMN_ORDER = ALL_COLUMN_DEFS.map((column) => column.key);

export const DEFAULT_COLUMN_VISIBILITY = Object.fromEntries(
    ALL_COLUMN_DEFS.map((column) => [column.key, Boolean(column.defaultVisible)])
) as Record<ColumnKey, boolean>;

const STORAGE_KEY = "availsPlusColumnPrefs";

export interface ColumnPreferences {
    order: ColumnKey[];
    visible: Record<string, boolean>;
}

function isColumnKey(value: string): value is ColumnKey {
    return DEFAULT_COLUMN_ORDER.includes(value as ColumnKey);
}

export function loadColumnPreferences(): ColumnPreferences {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return {
                order: [...DEFAULT_COLUMN_ORDER],
                visible: { ...DEFAULT_COLUMN_VISIBILITY },
            };
        }
        const parsed = JSON.parse(raw) as Partial<ColumnPreferences>;
        const savedOrder = Array.isArray(parsed.order)
            ? parsed.order.filter(isColumnKey)
            : [];
        const order = [
            ...savedOrder,
            ...DEFAULT_COLUMN_ORDER.filter((key) => !savedOrder.includes(key)),
        ];
        const visible = {
            ...DEFAULT_COLUMN_VISIBILITY,
            ...(parsed.visible ?? {}),
        };
        return { order, visible };
    } catch {
        return {
            order: [...DEFAULT_COLUMN_ORDER],
            visible: { ...DEFAULT_COLUMN_VISIBILITY },
        };
    }
}

export function saveColumnPreferences(prefs: ColumnPreferences) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
}

export { LICENSE_TYPE_OPTIONS, FORMAT_PROFILE_OPTIONS };
